import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import FullWidthModal from '@components/common/Modal/FullWidthModal';
import CheckDetails from './CheckDetails';
import ActionButtons from './ActionButtons';
import ModalHeader from '@components/common/ModalHeader';
import { LABEL } from '@veriables';
import { syncCheck, printing } from '@reducers/storeChecks';
import {
  setErrorPopUp,
  setNotification,
  setPrintNotification,
} from '@reducers/notification';
import useMoreActionsController from './useMoreActionsController';
import { KEYS } from './ActionButtons/constants';
import { useMemo } from 'react';
import getDeviceType from '@utility/deviceType';
import { getObjectValue } from '@utility/utills';
import style from './moreActions.module.scss';

const MoreActions = ({
  serverAppTheme = {},
  isOpen = false,
  closeTools = () => {},
  data = {},
  authId,
  authInfo,
  deviceInfo,
  userInfo,
  syncCheck: syncCheckAction,
  printing: printingAction,
  setErrorPopUp,
  setNotification,
  setPrintNotification,
  nonIntegratedPos,
  thirdPartyAppName,
  isOnlyOrderMode = false,
}) => {
  const { onActionClick, modalsJSX } = useMoreActionsController({
    data,
    authId,
    authInfo,
    deviceInfo,
    userInfo,
    serverAppTheme,
    closeTools,
    syncCheckAction,
    setErrorPopUp,
    printingAction,
    setNotification,
    setPrintNotification,
  });

  const { isSpeedOrder } = useMemo(() => getDeviceType(), [deviceInfo]);

  const isDefaultMode = useMemo(
    () =>
      getObjectValue(
        () =>
          !isSpeedOrder &&
          (!deviceInfo?.deviceGroup ||
            [0].includes(deviceInfo?.deviceGroup?.startMode)),
        false
      ),
    [isSpeedOrder, deviceInfo?.deviceGroup]
  );
  const buttonVisibility = useMemo(
    () => ({
      [KEYS.SYNC_CHECK]: true,
      [KEYS.REPOST_PAYMENT]: !nonIntegratedPos,
      [KEYS.LAUNCH_EXTERNAL_APP]:
        data?.open && thirdPartyAppName && authInfo.thirdPartyAppUrl && !isOnlyOrderMode,
      [KEYS.ATTACH_TO_PARTY]: authInfo?.checkActionInfo?.menuItemId,
      [KEYS.TABLE]: true,
      [KEYS.EMPLOYEE]: data?.open && nonIntegratedPos && !isOnlyOrderMode,
      [KEYS.VOID_CHECK]: data?.open && !isOnlyOrderMode && authInfo.nonIntegratedPos
        ? authInfo?.jobs?.filter(
          item => item?.id === userInfo?.jobId
        )[0]?.allowVoid
        : true,
      [KEYS.MERGE_CHECK]: data?.open && nonIntegratedPos && !isOnlyOrderMode,
      [KEYS.GAME_CARD]: !!authInfo?.gamingSystemConfig && !isOnlyOrderMode,
      [KEYS.ADD_LOYALTY]: authInfo?.loyaltyInfo?.supportsLoyalty,
      [KEYS.GIFT_CARD]: authInfo?.loyaltyInfo?.supportsGiftCardSale && !isOnlyOrderMode,
      [KEYS.RECEIPT]: window?.location?.pathname.includes('checks'),
      [KEYS.PRINT]:
        isDefaultMode && deviceInfo?.deviceGroup?.receiptTypes?.print,
    }),
    [
      nonIntegratedPos,
      data?.open,
      thirdPartyAppName,
      authInfo?.thirdPartyAppUrl,
      authInfo?.checkActionInfo?.menuItemId,
      isDefaultMode,
      deviceInfo?.deviceGroup?.receiptTypes?.print,
    ]
  );

  const buttonLabels = useMemo(
    () => ({
      [KEYS.TABLE]: data?.tab ? LABEL().TRANSFER_TAB : LABEL().TRANSFER_TABLE,
    }),
    [data?.tab]
  );

  const buttonDisable = useMemo(
    () => ({
      [KEYS.SYNC_CHECK]: false,
      [KEYS.REPOST_PAYMENT]:
        data?.tickets?.length <= 0 || !data.open || data?.isOneDineCheck,
      [KEYS.VOID_CHECK]:
        (!data?.isOneDineCheck && !nonIntegratedPos) || !data?.open,
      [KEYS.PRINT]: data?.PendingItems?.length > 0,
      [KEYS.TABLE]:
        (nonIntegratedPos ? false : !data?.isOneDineCheck) || !data?.open,
      [KEYS.MERGE_CHECK]: data?.PendingItems?.length > 0,
      [KEYS.ATTACH_TO_PARTY]: false,
      [KEYS.GIFT_CARD]: data?.isOneDineCheck || !!data?.PendingItems?.length || !data?.open,
      [KEYS.GAME_CARD]: data?.isOneDineCheck || !!data?.PendingItems?.length,
      [KEYS.ADD_LOYALTY]: !!data?.PendingItems?.length || !data?.open,
      [KEYS.LAUNCH_EXTERNAL_APP]:
        data?.isOneDineCheck || !!data?.PendingItems?.length,
      [KEYS.EMPLOYEE]: !!data?.PendingItems?.length,
      [KEYS.RECEIPT]: (data?.paidAmount && Number(data?.paidAmount)) === 0
    }),
    [data, nonIntegratedPos]
  );

  return (
    <div className="h-100">
      <FullWidthModal
        isOpen={isOpen}
        customClass2="h-100"
        style={{
          backgroundColor: serverAppTheme?.popUp_bg,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ModalHeader tileHeader={LABEL().MORE_FUNCTION_TITLE} headerClassName={style.moreTitle}/>

        <div className="col-12 px-0" style={{ flex: '1 1 auto', minHeight: 0 }}>
          <CheckDetails
            serverAppTheme={serverAppTheme}
            isTab={data?.tab}
            tabName={data?.tabName}
            stationName={data?.stationName}
            openDate={data?.openDate}
            totalAmount={data?.totalAmount}
            modalNumber={data?.number}
            paidAmount={data?.paidAmount}
            closeDate={data?.closeDate}
            employeeFirstName={data?.employeeFirstName}
            employeeLastName={data?.employeeLastName}
            unpaidAmount={data?.unpaidAmount}
            checkOpen={data?.open}
            checkData={data}
          />
        </div>

        <div className="col-12 mt-2 px-1 py-1" style={{ flex: '0 0 auto' }}>
          <ActionButtons
            serverAppTheme={serverAppTheme}
            onCancel={closeTools}
            onActionClick={onActionClick}
            buttonDisable={buttonDisable}
            buttonVisibility={buttonVisibility}
            buttonLabels={buttonLabels}
            thirdPartyAppName={thirdPartyAppName}
          />
        </div>
      </FullWidthModal>

      {modalsJSX}
    </div>
  );
};

const mapStateToProps = ({ oauth }) => ({
  authId: oauth.authInfo?.id || null,
  authInfo: oauth.authInfo,
  nonIntegratedPos: oauth.authInfo?.nonIntegratedPos,
  deviceInfo: oauth.deviceInformation,
  userInfo: oauth.loggedInUserData,
  thirdPartyAppName: oauth?.authInfo?.thirdPartyAppName,
  isOnlyOrderMode: oauth.deviceInformation?.deviceGroup?.serverAppSettings?.disableOrdering
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      syncCheck,
      printing,
      setErrorPopUp,
      setNotification,
      setPrintNotification,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(MoreActions);
